import { Axis } from '../../spatial-classes';
import { BlueprintInstanceType } from '../shared.enums';
import { dimensionData } from './dimension.data';
import { Publish } from '../../decorators';
import { DimensionDataIn, DimensionDataOut } from './types';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';

export class Dimension extends KitchenDesignBase<DimensionDataIn> {
	readonly sourceId: string | null;
	readonly targetId: string | null;
	readonly axis: Axis;
	#isVisible: boolean;
	#lineFactor: number;
	#labelFactor: number;
	#customAngle: number | null;
	#dragOffset: { x: number, y: number } | null;

	#manuallyHidden: boolean;
	#manualVisibility: boolean | null;

	constructor (options:KitchenDesignOptions<DimensionDataIn> = {}) {
		super(BlueprintInstanceType.Dimension, options);

		const { dataIn = null } = options;
		const data = dimensionData(dataIn);
		this._id = data.id;
		this.sourceId = data.sourceId;
		this.targetId = data.targetId;
		this.axis = new Axis({ dataIn: data.axis });
		this.#isVisible = data.isVisible;
		this.#manuallyHidden = data.manuallyHidden;
		this.#manualVisibility = data.manualVisibility;
		this.#lineFactor = data.lineFactor;
		this.#labelFactor = data.labelFactor;
		this.#customAngle = data.customAngle;
		this.#dragOffset = data.dragOffset;

		// console.log('Dimension:constructor', this._isNew, data);
		this.setInitialized();
	}

	@Publish('dimension', 'updated')
	set isVisible (value: boolean) {
		this.#manuallyHidden = true;
		this.#manualVisibility = value;
		this.#isVisible = value;
	}

	get isVisible (): boolean {
		return this.#isVisible;
	}

	@Publish('dimension', 'updated')
	set autoVisible (value: boolean) {
		if (this.#manualVisibility !== null) return;
		this.#isVisible = value;
	}

	/** Rule-driven visibility; a user's own show/hide wins and locks this out */
	get autoVisible (): boolean { return this.#isVisible; }

	get manuallyHidden (): boolean { return this.#manuallyHidden; }

	/** What the user chose, null until they show or hide this dimension themselves */
	get manualVisibility (): boolean | null { return this.#manualVisibility; }

	get lineFactor (): number { return this.#lineFactor; }
	@Publish('dimension-linefactor', 'updated')
	set lineFactor (value: number) {
		if (value !== this.#lineFactor) {
			// console.log('Dimension:set:lineFactor', { old: this.#lineFactor, new: value });
			this.#lineFactor = value;
		}
	}

	get labelFactor (): number { return this.#labelFactor; }
	@Publish('dimension-labelfactor', 'updated')
	set labelFactor (value: number) {
		if (value !== this.#labelFactor) {
			// console.log('Dimension:set:labelFactor', { old: this.#labelFactor, new: value });
			this.#labelFactor = value;
		}
	}

	get customAngle (): number | null { return this.#customAngle; }
	@Publish('dimension_custom_angle', 'updated')
	set customAngle (value: number | null) {
		this.#customAngle = value;
	}

	get dragOffset (): { x: number, y: number } | null { return this.#dragOffset; }
	@Publish('dimension_drag_offset', 'updated')
	set dragOffset (value: { x: number, y: number } | null) {
		this.#dragOffset = value;
	}

	/**
	 * Rotates the dimension by 90 degrees, cycling through 0, 90, 180, 270
	 */
	public rotate90 (): void { this.customAngle = ((this.#customAngle ?? 0) + 90) % 360; }

	public serialize (): DimensionDataOut {
		return {
			id: this.id,
			sourceId: this.sourceId,
			targetId: this.targetId,
			axis: this.axis.serialize(),
			isVisible: this.isVisible,
			manuallyHidden: this.#manuallyHidden,
			manualVisibility: this.#manualVisibility,
			lineFactor: this.lineFactor,
			labelFactor: this.labelFactor,
			customAngle: this.#customAngle,
			dragOffset: this.#dragOffset
		};
	}
}


